import { useMemo, useRef, useState } from 'react';

import { Button, Stack, TitleGroup } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
// eslint-disable-next-line no-restricted-imports -- AddMemberForm lives in the team feature; this cross-feature import is intentional for the onboarding shell.
import { AddMemberForm, type AddMemberFormRef } from '@features/team/components/AddMemberForm';

import { useUpdateOnboardingPage } from '../../api/configuration/useUpdateOnboardingPage';

import { onboardingContent, OnboardingPage } from './content';

import './AccessPage.scss';

export function AccessPage() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const { mutateAsync: updateOnboardingPage } = useUpdateOnboardingPage();
	const formRef = useRef<AddMemberFormRef>(null);
	const [hasValidEmail, setHasValidEmail] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const pageContent = onboardingContent[OnboardingPage.Access];

	// Calculate next page (Access is page 6, next is Completion page 7)
	const totalPages = useMemo(() => Object.keys(onboardingContent).length, []);
	const currentPage = servicesProvider?.onboardingPage || OnboardingPage.Access;
	const isLastPage = currentPage === totalPages;
	const nextPage = isLastPage ? currentPage : currentPage + 1;

	const handleSuccess = async (): Promise<void> => {
		if (servicesProvider?.id) {
			try {
				await updateOnboardingPage({
					page: nextPage,
					onboardingCompleted: isLastPage,
				});
			} catch (error) {
				console.error('Failed to navigate to next onboarding page:', error);
			}
		}
	};

	const handleSubmit = async (): Promise<void> => {
		await formRef.current?.submit();
	};

	if (!pageContent) {
		return null;
	}

	return (
		<Stack itemsSpacing='24' itemsAlignX='center'>
			<TitleGroup
				title={pageContent.pageTitle}
				description={pageContent.subtitle}
				level='page'
				variant='centered'
				maxWidth='640px'
			/>
			<Stack itemsSpacing='16' className='access-page__form'>
				<AddMemberForm
					ref={formRef}
					onSuccess={handleSuccess}
					onValidationChange={(valid, loading) => {
						setHasValidEmail(valid);
						setIsLoading(loading);
					}}
				/>
				<Button
					size='large'
					isFullWidth
					onPress={() => {
						void handleSubmit();
					}}
					isDisabled={!hasValidEmail}
					isLoading={isLoading}>
					Send invites
				</Button>
			</Stack>
		</Stack>
	);
}
